import * as React from 'react';
import { useState, useEffect } from 'react';
import { PieChart } from '@mui/x-charts/PieChart';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { getAllSessions } from '../api/sessions';
import dayjs from 'dayjs';

export default function ChartUserByBarber({ selectedDate }) {
  const [loading, setLoading] = useState(true);
  const [barberData, setBarberData] = useState([]);
  const [totalSessions, setTotalSessions] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getAllSessions();

        // Define date range (last 14 days from selectedDate)
        const endDate = new Date(selectedDate);
        endDate.setHours(23, 59, 59, 999);
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 13);
        startDate.setHours(0, 0, 0, 0);

        // Filter sessions within the date range
        const filteredData = data.filter(session => {
          const sessionDate = new Date(session.date);
          return sessionDate >= startDate && sessionDate <= endDate;
        });

        // Count sessions per barber
        const barberCount = {};
        filteredData.forEach(session => {
          if (session.barber_name) {
            barberCount[session.barber_name] = (barberCount[session.barber_name] || 0) + 1;
          }
        });

        // Convert to array format for PieChart
        const total = filteredData.length;
        const barberArray = Object.entries(barberCount).map(([name, value]) => ({
          label: name,
          value,
        }));

        setBarberData(barberArray);
        setTotalSessions(total);
      } catch (error) {
        console.error('Error fetching session data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDate]);

  const colors = ['hsl(220, 20%, 65%)', 'hsl(220, 20%, 42%)', 'hsl(220, 20%, 35%)', 'hsl(220, 20%, 25%)'];

  return (
    <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1, minWidth: 240 }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2">
          Sessions by Barber (Last 14 Days)
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PieChart
            colors={colors}
            margin={{ left: 80, right: 80, top: 80, bottom: 80 }}
            series={[{ data: barberData, innerRadius: 50, outerRadius: 60 }]}
            height={200}
            width={200}
            slotProps={{ legend: { hidden: true } }}
          >
            <text x="50%" y="45%" textAnchor="middle" fontSize="18px" fontWeight="bold">
              {totalSessions}
            </text>
            <text x="50%" y="60%" textAnchor="middle" fontSize="12px" fill="gray">
              Total Sessions
            </text>
          </PieChart>
        </Box>
        {barberData.map((barber, index) => (
          <Stack key={index} direction="row" sx={{ alignItems: 'center', gap: 2, pb: 2 }}>
            <Stack sx={{ gap: 1, flexGrow: 1 }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: '500' }}>
                  {barber.label}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {((barber.value / totalSessions) * 100).toFixed(1)}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={(barber.value / totalSessions) * 100}
                sx={{ [`& .${linearProgressClasses.bar}`]: { backgroundColor: colors[index % colors.length] } }}
              />
            </Stack>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}